import { useEffect, useId, useRef } from 'react';
import { currencySymbol, type CurrencyCode } from '@/shared/lib/format';
import { Label } from '@/shared/ui/label';
import { cn } from '@/shared/lib/cn';
import { maskCurrency } from '@/shared/lib/input-masks';

interface OperatorMoneyFieldProps {
  id?: string;
  label?: string;
  value: string;
  onChange: (masked: string) => void;
  /** Moeda do passbook (hasPT/ES/EN → EUR; hasIN → GBP). */
  currencyCode?: CurrencyCode;
  placeholder?: string;
  hint?: string;
  invalid?: boolean;
  autoFocus?: boolean;
  className?: string;
  inputClassName?: string;
  size?: 'md' | 'lg';
}

export function OperatorMoneyField({
  id: idProp,
  label,
  value,
  onChange,
  currencyCode = 'BRL',
  placeholder = '0,00',
  hint,
  invalid,
  autoFocus,
  className,
  inputClassName,
  size = 'lg',
}: OperatorMoneyFieldProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const inputRef = useRef<HTMLInputElement>(null);
  const symbol = currencySymbol(currencyCode);

  useEffect(() => {
    if (!autoFocus) return;
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 320);
    return () => window.clearTimeout(t);
  }, [autoFocus]);

  return (
    <div className={cn('shrink-0 space-y-2 scroll-mt-4 scroll-mb-28', className)}>
      {label ? <Label htmlFor={id}>{label}</Label> : null}

      {/* Wrapper carrega o chrome (borda/foco). Input interno sem field-glass. */}
      <div
        className={cn(
          'field-glass flex shrink-0 items-center gap-2 rounded-2xl border px-4 transition-[box-shadow,border-color]',
          size === 'lg' ? 'h-14 min-h-14' : 'h-11 min-h-11',
          invalid
            ? 'border-destructive'
            : 'border-[hsl(var(--glass-border)/0.45)] focus-within:border-primary/50 focus-within:shadow-[0_0_0_3px_hsl(var(--ring)/0.22)]',
        )}
      >
        <span className="shrink-0 text-sm font-semibold text-muted-foreground">{symbol}</span>
        <input
          ref={inputRef}
          id={id}
          value={value}
          onChange={(e) => onChange(maskCurrency(e.target.value))}
          placeholder={placeholder}
          inputMode="decimal"
          autoComplete="off"
          aria-invalid={invalid}
          className={cn(
            'min-w-0 flex-1 bg-transparent py-0 pl-0 pr-1.5 text-right font-semibold tabular-nums text-foreground',
            'outline-none placeholder:font-normal placeholder:text-muted-foreground/60',
            size === 'lg' ? 'text-2xl leading-none' : 'text-base leading-none',
            inputClassName,
          )}
        />
      </div>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
